import { Alert, Box } from "@mui/material"
import type { FC } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import { PAST_ACCOUNTING_PERIOD_WARNING } from "../../constants"
import { useGetVendorPaymentTerm } from "../../hooks/useGetVendorPaymentTerm"
import type { VendorOption } from "../../hooks/useVendorOptions"
import type { InvoiceFormValues } from "../../schemas/invoiceForm"
import type { InvoiceFormDialogMode } from "../../types/dialog"
import { AccountingPeriodStatus, formatAccountingPeriod, getAccountingPeriodStatus } from "../../utils/accountingPeriod"

import { ExpenseLinesSection } from "./ExpenseLinesSection"
import { InvoiceAttachmentsSection } from "./InvoiceAttachmentsSection"
import { VendorAutocomplete } from "./VendorAutocomplete"

import { AerosTextField } from "@/components/UiKit/AerosTextField/AerosTextField"
import { FormCheckbox } from "@/components/UiKit/FormControls/FormCheckbox"
import { FormDatePicker } from "@/components/UiKit/FormControls/FormDatePicker"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import type { UploadRowStatus } from "@/components/upload/MultiFileUpload/types"
import { US_COUNTRY_ISO_CODE } from "@/constants/locations"
import type { Invoice } from "@/graphql/codegen/graphql"
import type { UploadRowProgress } from "@/hooks/useMultiFileUpload"

interface InvoiceFormProps {
  mode: InvoiceFormDialogMode
  invoice?: Invoice
  readOnly?: boolean
  selectedVendorOption?: VendorOption | null
  onSelectedVendorOptionChange?: (option: VendorOption | null) => void
  attachmentStatuses?: Record<string, UploadRowStatus>
  attachmentProgress?: Record<string, UploadRowProgress>
  isUploadingAttachments?: boolean
}

// AK-5093: a vendor billed outside the US forces Printed Check. A missing country is not treated as non-US here,
// the backend decides that case on save.
const isNonUsBillingCountry = (billingCountry: string | undefined): boolean =>
  Boolean(billingCountry) && billingCountry !== US_COUNTRY_ISO_CODE

export const InvoiceForm: FC<InvoiceFormProps> = ({
  mode,
  invoice,
  readOnly,
  selectedVendorOption,
  onSelectedVendorOptionChange,
  attachmentStatuses,
  attachmentProgress,
  isUploadingAttachments,
}) => {
  const { control, setValue } = useFormContext<InvoiceFormValues>()
  const selectedVendorId = useWatch({ control, name: "vendorId" })
  const invoiceDate = useWatch({ control, name: "invoiceDate" })
  const { vendorPaymentTermName, isFetching: isPaymentTermFetching } = useGetVendorPaymentTerm(selectedVendorId)

  const handleVendorChange = (option: VendorOption | null) => {
    setValue("printedCheck", isNonUsBillingCountry(option?.billingCountry), { shouldValidate: true, shouldDirty: true })
    onSelectedVendorOptionChange?.(option)
  }

  // AK-7187: the country comes from the vendor the user picked, never from a vendor list the form loads itself:
  // the vendor autocomplete searches on the server, so a vendor found by search is often missing from any other list
  // and the lock would silently not apply. On edit/view the saved vendor comes from the loaded invoice for as long as
  // the form still points at it.
  const isInvoiceVendorSelected = Boolean(invoice && invoice.vendorCompanyId === selectedVendorId)
  const invoiceVendorBillingCountry = invoice?.vendorCompany?.locationsAssociations?.find(
    (association) => association.billing
  )?.billing?.countryIsoCode
  const billingCountry = isInvoiceVendorSelected
    ? (invoiceVendorBillingCountry ?? undefined)
    : selectedVendorOption?.billingCountry
  const isPrintedCheckLocked = isNonUsBillingCountry(billingCountry)

  const isReadOnly = readOnly ?? mode === "view"
  const isView = mode === "view"
  const isCommentRequired = mode !== "create"

  return (
    <Box className='flex flex-col gap-6'>
      <VendorAutocomplete
        disabled={isReadOnly}
        invoice={invoice}
        selectedVendor={selectedVendorOption}
        onVendorChange={handleVendorChange}
      />

      <Box className='grid grid-cols-2 gap-4'>
        <AerosTextField
          label='Payment Terms'
          value={vendorPaymentTermName ?? ""}
          placeholder={isPaymentTermFetching ? undefined : "Automatically populated from the selected vendor"}
          disabled
          data-testid='invoice.form.payment-terms-field'
        />
        <FormTextField
          control={control}
          name='invoiceNumber'
          label='Vendor Invoice #'
          required
          disabled={isReadOnly}
          slotProps={{ htmlInput: { maxLength: 15 } }}
          data-testid='invoice.form.invoice-number-field'
        />
      </Box>

      <Box className='flex flex-col gap-2'>
        <Box className='grid grid-cols-3 gap-4'>
          <FormDatePicker
            control={control}
            name='invoiceDate'
            label='Invoice Date or Date Received'
            required
            disabled={isReadOnly}
            data-testid='invoice.form.invoice-date-field'
          />
          <AerosTextField
            label='Accounting Period'
            value={formatAccountingPeriod(invoiceDate) ?? ""}
            disabled
            data-testid='invoice.form.accounting-period-field'
          />
          <FormDatePicker
            control={control}
            name='dueDate'
            label='Due Date'
            required
            disabled={isReadOnly}
            data-testid='invoice.form.due-date-field'
          />
        </Box>
        {getAccountingPeriodStatus(invoiceDate) === AccountingPeriodStatus.Past && (
          <Alert severity='warning' className='py-1 text-xs' data-testid='invoice.form.past-accounting-period-alert'>
            {PAST_ACCOUNTING_PERIOD_WARNING}
          </Alert>
        )}
      </Box>

      <Box className='grid grid-cols-2 items-end gap-4'>
        <FormNumericField
          control={control}
          name='totalAmount'
          label='Total Amount to Pay'
          required
          prefix='$'
          decimalScale={2}
          allowNegative={false}
          disabled={isReadOnly}
          data-testid='invoice.form.total-amount-field'
        />
        <Box className='flex h-[56px] items-center'>
          <FormCheckbox
            control={control}
            name='printedCheck'
            label='Printed Check'
            disabled={isPrintedCheckLocked || isReadOnly}
            data-testid='invoice.form.printed-check-field'
          />
        </Box>
      </Box>

      <ExpenseLinesSection disabled={isReadOnly} />

      {!isView && (
        <FormTextField
          control={control}
          name='comment'
          label='Comment'
          required={isCommentRequired}
          disabled={isReadOnly}
          slotProps={{ htmlInput: { maxLength: 500 } }}
          data-testid='invoice.form.comment-field'
        />
      )}

      <InvoiceAttachmentsSection
        invoiceId={invoice?.invoiceId}
        disabled={isReadOnly}
        statuses={attachmentStatuses}
        progress={attachmentProgress}
        isUploading={isUploadingAttachments}
      />
    </Box>
  )
}
